import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import { PrismicNextImage } from 'components/PRISMIC/PrismicNextImage';
import ContentContainer from 'components/content-container';
import mixpanel from 'lib/analytics/mixpanel';

import type { JSX } from 'react';

/**
 * Props for `SingleCard`.
 */
export type SingleCardProps = SliceComponentProps<Content.SingleCardSlice>;

/**
 * Component for "SingleCard" Slices.
 */
const SingleCard = ({ slice }: SingleCardProps): JSX.Element => {
  const isDefault = slice.variation === 'default';

  if (slice.primary.is_hidden) {
    return null;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white pb-16 md-tui:pb-20"
    >
      <ContentContainer
        className="mx-auto max-w-7xl px-5 sm-tui:px-10"
        noPadding
      >
        {isDefault && <hr />}

        <div className="my-6">
          <Text variant="header2" useNewTextStyles>
            {slice.primary.title}
          </Text>
        </div>
        <div
          className={cx('flex flex-col md-tui:gap-10', {
            'md-tui:flex-row': slice.primary.imageAlignment === 'Left',
            'md-tui:flex-row-reverse': slice.primary.imageAlignment === 'Right'
          })}
        >
          <div className="flex-1">
            <PrismicConditionalLink
              field={slice.primary.imageLink}
              onClick={() =>
                mixpanel.track('slice_click', {
                  current_url: window.location.href,
                  title: slice.primary.title.toLowerCase().replace(' ', '_'),
                  side_title: '',
                  item_number: '',
                  link_type: 'image'
                })
              }
            >
              <PrismicNextImage
                field={slice.primary.img}
                style={{ margin: 'auto', width: '100%' }}
              />
            </PrismicConditionalLink>
          </div>
          <div className="flex flex-1 flex-col">
            <Text
              variant="subheader1"
              useNewTextStyles
              UNSAFE_className="mb-5 mt-10 md-tui:mb-5 sm-tui:mt-5 md-tui:mt-0"
            >
              {slice.primary.sideText}
            </Text>

            <div className="flex flex-col gap-4">
              {slice.items.map((item, index) => (
                <PrismicConditionalLink
                  field={item.link}
                  onClick={() =>
                    mixpanel.track('slice_click', {
                      current_url: window.location.href,
                      title: slice.primary.title
                        .toLowerCase()
                        .replace(' ', '_'),
                      side_title: '',
                      item_number: index + 1,
                      link_type: 'link'
                    })
                  }
                >
                  <Text
                    variant="link1"
                    useNewTextStyles
                    color={{ isOnLightSurface: true }}
                    weight="font-semibold"
                    UNSAFE_className="cursor-pointer underline"
                  >
                    {item.text}
                  </Text>
                </PrismicConditionalLink>
              ))}
            </div>
          </div>
        </div>
      </ContentContainer>
    </section>
  );
};

export default SingleCard;
